import qs from "qs"
import { Fragment, useCallback, useEffect, useRef, useState } from "react"
import { LoaderCircleIcon, PencilIcon, PlusIcon, Trash2Icon, XIcon } from "lucide-react"
import { PopoverClose } from "@radix-ui/react-popover"
import { Outlet, useFetcher, useRouteLoaderData } from "react-router"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { getOrganisations } from "@/utils/projects"
import { fetchAPI, handleResponse, throwIfNotOk } from "@/utils"
import { FiltersWrapper, type StoredSession } from "@/components/Filters"
import type { RootLoader } from "@/routes/root"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Badge } from "@/components/ui/badge"
import { hasPermission } from "@/machines/Authmachine"
import { ValidationSummary } from "@/components/ui/ValidationSummary"

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { formatRHFErrors, formatFluentValidationErrors, type FluentValidationError } from "@/utils/forms"

const routeId = "metadata-usage-routeId"
const sessionKey = "metadata-usage-filter"

export interface MetadataUsage {
  name: string
  properties: string[]
}

const PropertySchema = z
  .string()
  .min(1, "Please enter at least 1 character")
  .max(50, "Property names should be a maximum of 50 characters")
  .regex(/^[a-zA-Z0-9 ]+$/, "Only alpha numeric and space characters are allowed")

const PropertyArraySchema = z
  .array(PropertySchema)
  .min(1, "At least one property is required")
  .superRefine((val, ctx) => {
    const unique = new Set(val.map((v) => v.toLowerCase()))
    if (unique.size !== val.length) {
      ctx.addIssue({
        code: "custom",
        message: "Duplicate properties are not allowed",
      })
    }
  })

const createValidationSchema = <T extends "CREATE" | "UPDATE">(action: T) => {
  return z.object({
    action: z.literal(action),
    organisation: z.string().min(1),
    name: z
      .string()
      .min(1, "Name is required")
      .max(50, "Name should be a maximum of 50 characters")
      .regex(/^[a-zA-Z0-9 ]+$/, "Only alpha numeric and space characters are allowed"),
    // Validate the array itself
    properties: PropertyArraySchema,
  })
}

const MetadataUsageCreateSchema = createValidationSchema("CREATE")

const MetadataUsageUpdateSchema = createValidationSchema("UPDATE")

const MetadataUsageDeleteSchema = z.object({
  action: z.literal("DELETE"),
  organisation: z.string().min(1),
  name: z.string().min(1),
})

const MetadataUsageSchema = z.discriminatedUnion("action", [
  MetadataUsageCreateSchema,
  MetadataUsageUpdateSchema,
  MetadataUsageDeleteSchema,
])

interface LoaderReturn {
  usages: MetadataUsage[]
  loadedOrganisation: string
}

interface ActionReturn {
  action: "CREATE" | "UPDATE" | "DELETE"
  errorMessage?: string
  errors: Record<string, string[]>
}

async function loader(): Promise<LoaderReturn> {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const sessionData = sessionStorage.getItem(sessionKey)
  const filters =
    sessionData != null && sessionData.length > 0 ? (qs.parse(sessionData) as unknown as Partial<StoredSession>) : {}
  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]

  try {
    const response = await fetchAPI<Record<string, string[]>>(
      `/api/UnityViewer/MetadataUsage/${loadedOrganisation}`,
    ).then(handleResponse)

    const usages: MetadataUsage[] = Object.entries(response).map(([name, properties]) => ({
      name,
      properties,
    }))

    return {
      usages,
      loadedOrganisation,
    }
  } catch (error) {
    if (error != null && typeof error === "object" && "status" in error && error.status === 404) {
      return { usages: [], loadedOrganisation }
    }
    throw error
  }
}

async function action({ request }: { request: Request }): Promise<ActionReturn> {
  const { data, success, error } = MetadataUsageSchema.safeParse(await request.json())

  if (!success) {
    console.error(error)
    return {
      action: "CREATE",
      errors: {},
      errorMessage: "Failed to parse form data into metadata usage schema.",
    }
  }

  let errorMessage: string | undefined
  let errors: Record<string, string[]> = {}

  switch (data.action) {
    case "CREATE":
    case "UPDATE": {
      try {
        await fetchAPI(`/api/UnityViewer/MetadataUsage/${data.organisation}/${data.name}`, {
          method: "POST",
          body: JSON.stringify(data.properties),
        }).then(throwIfNotOk)
      } catch (error) {
        const response = error as Response
        if (response.status === 400) {
          errors = formatFluentValidationErrors((await response.json()) as FluentValidationError[])
        } else {
          console.error(error)
          errorMessage = data.action === "CREATE" ? "Error creating metadata usage" : "Error updating metadata usage"
        }
      }
      break
    }
    case "DELETE": {
      try {
        await fetchAPI(`/api/UnityViewer/MetadataUsage/${data.organisation}/${data.name}`, {
          method: "DELETE",
        }).then(throwIfNotOk)
      } catch (error) {
        const response = error as Response
        if (response.status === 400) {
          const errorData = (await response.json()) as { errors: Record<string, string[]> }
          errors = errorData.errors
        } else {
          console.error(error)
          errorMessage = "Error creating metadata usage"
        }
      }
      break
    }
    default:
      throw new Error("Unknown action")
  }

  return { action: data.action, errorMessage, errors }
}

function Main() {
  const data = useRouteLoaderData<LoaderReturn>(routeId)
  if (data == null) return null

  return (
    <div className="h-screen overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12" />
            <TableHead className="text-left">Usage Name</TableHead>
            <TableHead className="text-left">Properties Count</TableHead>
            <TableHead className="w-24">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.usages.length > 0 ? (
            data.usages.map((item, index) => (
              <TableRow key={item.name} className="text-sm">
                <TableCell>{index + 1}.</TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.properties.length}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <MetadataUsageEditOrCreate usage={item} />
                    <MetadataUsageDelete usage={item} />
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} className="py-4 text-center text-gray-500">
                No metadata usages found for this organisation.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Outlet />
    </div>
  )
}

function MetadataUsageEditOrCreate({ usage }: { usage?: MetadataUsage }) {
  const data = useRouteLoaderData<LoaderReturn>(routeId)
  const [open, setOpen] = useState(false)

  const isEditing = usage != null

  const closeDialog = useCallback(() => {
    setOpen(false)
  }, [])

  if (data == null || hasPermission("writeAccess") !== true) return null

  // Helper to render the actual button
  const TriggerButton = isEditing ? (
    <Button variant="ghost" size="icon" className="h-8 w-8">
      <PencilIcon className="h-4 w-4" />
      <span className="sr-only">Edit Metadata Usage</span>
    </Button>
  ) : (
    <Button variant="outline">
      <PlusIcon className="mr-2 h-4 w-4" /> Add New
    </Button>
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {isEditing ? (
        /* Outer: Tooltip -> Middle: TooltipTrigger -> Inner: DialogTrigger */
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>{TriggerButton}</DialogTrigger>
          </TooltipTrigger>
          <TooltipContent>Edit Metadata Usage</TooltipContent>
        </Tooltip>
      ) : (
        <DialogTrigger asChild>{TriggerButton}</DialogTrigger>
      )}

      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit" : "Add New"} Metadata Usage</DialogTitle>
          <DialogDescription>
            {isEditing ? (
              <>
                Manage properties for <span className="font-semibold">{usage.name}</span>
              </>
            ) : (
              "Create a new metadata usage with properties"
            )}
          </DialogDescription>
        </DialogHeader>

        <MetadataUsageEditOrCreateForm usage={usage} closeDialog={closeDialog} />
      </DialogContent>
    </Dialog>
  )
}

function MetadataUsageEditOrCreateForm({ usage, closeDialog }: { usage?: MetadataUsage; closeDialog: () => void }) {
  const fetcher = useFetcher<ActionReturn>()
  const data = useRouteLoaderData<LoaderReturn>(routeId)
  const propertyRef = useRef<HTMLInputElement>(null)

  const isEditing = usage !== undefined

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof MetadataUsageSchema>>({
    resolver: zodResolver(MetadataUsageSchema),
    defaultValues: {
      action: isEditing ? "UPDATE" : ("CREATE" as const),
      organisation: data?.loadedOrganisation ?? "",
      name: usage?.name ?? "",
      properties: usage?.properties ?? [],
    },
  })

  const properties = watch("properties")

  // Combine both sources of errors into one "Summary" shape
  const zodSummary = formatRHFErrors(errors)
  const serverSummary = fetcher.data?.errors // Already in Record<string, string[]> format

  // Merge them (server errors usually take priority if both exist)
  const combinedErrors = { ...zodSummary, ...serverSummary }

  useEffect(() => {
    const result = fetcher.data
    if (result == null) return
    const hasNoValidationErrors = result.errors == null || Object.keys(result.errors).length === 0
    const hasNoGlobalError = result.errorMessage == null

    if (hasNoGlobalError && hasNoValidationErrors) {
      closeDialog()
      if (!isEditing) reset()
    }
  }, [closeDialog, isEditing, reset, fetcher.data])

  const handleAddProperty = useCallback(() => {
    if (propertyRef.current == null) return

    const value = propertyRef.current.value.trim()
    let data, success, error

    ;({ data, success, error } = PropertySchema.safeParse(value))

    if (!success) {
      setError("properties", { message: error?.issues[0].message })
      return
    }

    const newProperties = [...properties, data ?? ""]

    ;({ data, success, error } = PropertyArraySchema.safeParse(newProperties))

    if (!success) {
      setError("properties", { message: error?.issues[0].message })
      return
    }

    setValue("properties", newProperties, { shouldValidate: true })

    clearErrors("properties")
    propertyRef.current.value = ""
  }, [properties, setValue, setError, clearErrors])

  const handleDeleteProperty = useCallback(
    (property: string) => {
      const next = properties.filter((p) => p !== property)
      setValue("properties", next, { shouldValidate: true })
    },
    [properties, setValue],
  )

  const onSubmit = handleSubmit(async (formData) => {
    if (!isEditing) {
      try {
        const response = await fetchAPI<Record<string, string[]>>(
          `/api/UnityViewer/MetadataUsage/${data?.loadedOrganisation}/${formData.name}`,
        ).then(handleResponse)

        if (response != null && Object.keys(response).length > 0) {
          setError("name", { message: "That usage name already exists, please update the name to make it unique" })
          return
        }
      } catch (error) {
        const response = error as Response
        if (response == null || (response.status !== 404 && response.status !== 204)) {
          console.error(error)
          setError("name", { message: "There was a problem identifying if this is a duplicate entry or not" })
          return
        }
      }
    }

    clearErrors()
    fetcher.submit(formData, { method: "POST", encType: "application/json" })
  })

  if (data == null) return null

  return (
    <fetcher.Form method="POST" className="space-y-6" onSubmit={(e) => void onSubmit(e)}>
      <input type="hidden" {...register("action")} />
      <input type="hidden" {...register("organisation")} />

      <ValidationSummary errors={combinedErrors} />

      {isEditing ? (
        <div>
          <label className="mb-2 block text-sm font-medium">Name</label>
          <Input value={usage.name} disabled className="bg-gray-50" />
          <p className="mt-1 text-xs text-gray-500">Name cannot be changed after creation</p>
        </div>
      ) : (
        <div>
          <label className="mb-2 block text-sm font-medium">
            Name <span className="text-red-600">*</span>
          </label>
          <Input {...register("name")} placeholder="Enter usage name" />
        </div>
      )}

      <div>
        <label className="mb-2 block text-sm font-medium">
          Properties <span className="text-red-600">*</span>
        </label>

        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <Input
              ref={propertyRef}
              placeholder="Enter property name"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  handleAddProperty()
                }
              }}
            />
            <Button
              type="button"
              onClick={() => {
                handleAddProperty()
              }}
              className="shrink-0"
            >
              <PlusIcon className="mr-2 h-4 w-4" /> Add Property
            </Button>
          </div>

          {properties.length > 0 ? (
            <div className="rounded-md border p-4">
              <div className="mb-2 text-sm font-medium">Properties ({properties.length})</div>
              <div className="flex flex-wrap gap-2">
                {properties.map((property) => (
                  <Badge key={property} variant="secondary" className="flex items-center gap-1 pr-1 pl-3">
                    <span>{property}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        handleDeleteProperty(property)
                      }}
                      className="hover:text-destructive-foreground h-5 w-5 hover:bg-destructive"
                    >
                      <XIcon className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-dashed p-4 text-center text-sm text-gray-500">
              No properties added yet
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-4 border-t pt-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" />
            {isEditing ? "Updating..." : "Creating..."}
          </span>
        ) : (
          fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit">
          {isEditing ? <PencilIcon className="mr-2 h-4 w-4" /> : <PlusIcon className="mr-2 h-4 w-4" />}{" "}
          {isEditing ? "Update" : "Create"}
        </Button>
      </div>
    </fetcher.Form>
  )
}

function MetadataUsageDelete({ usage }: { usage: MetadataUsage }) {
  const data = useRouteLoaderData<LoaderReturn>(routeId)

  if (data == null || hasPermission("deleteAccess") !== true) return null

  return (
    <Popover>
      <Tooltip>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700">
              <Trash2Icon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent>Delete Metadata Usage</TooltipContent>
      </Tooltip>
      <PopoverContent side="right" align="start" className="max-w-sm">
        <p className="text-sm text-pretty">
          Metadata Usage &quot;<span className="font-semibold">{usage.name}</span>&quot; will be deleted. Are you sure?
        </p>
        <MetadataUsageDeleteForm usage={usage} />
      </PopoverContent>
    </Popover>
  )
}

function MetadataUsageDeleteForm({ usage }: { usage: MetadataUsage }) {
  const fetcher = useFetcher<ActionReturn>()
  const data = useRouteLoaderData<LoaderReturn>(routeId)

  const { register, handleSubmit } = useForm<z.infer<typeof MetadataUsageDeleteSchema>>({
    resolver: zodResolver(MetadataUsageDeleteSchema),
    defaultValues: {
      action: "DELETE" as const,
      organisation: data?.loadedOrganisation ?? "",
      name: usage.name,
    },
  })

  const onSubmit = handleSubmit((formData) => {
    fetcher.submit(formData, { method: "POST", encType: "application/json" })
  })

  if (data == null) return null

  return (
    <fetcher.Form
      method="POST"
      className="flex items-center justify-start gap-3 pt-4"
      onSubmit={(e) => void onSubmit(e)}
    >
      <input type="hidden" {...register("action")} />
      <input type="hidden" {...register("organisation")} />
      <input type="hidden" {...register("name")} />
      {fetcher.state !== "idle" && (
        <span className="flex items-center justify-start gap-1 pr-4">
          <LoaderCircleIcon className="size-4 animate-spin" /> Deleting
        </span>
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button variant="ghost">Cancel</Button>
      </PopoverClose>
      <Button type="submit" variant="destructive">
        Confirm
      </Button>
    </fetcher.Form>
  )
}

export function MetadataUsage() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRouteLoaderData<LoaderReturn>(routeId)

  return (
    <Fragment>
      {rootData != null && data != null && (
        <div className="flex items-center gap-4">
          {rootData.organisations.length > 1 && (
            <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey}>
              <FiltersWrapper.Select
                name="organisation"
                label="Organisation"
                defaultValue={data.loadedOrganisation}
                items={rootData.organisations}
              />
            </FiltersWrapper>
          )}
          <div className="flex-1" />
          <MetadataUsageEditOrCreate />
        </div>
      )}
      <Outlet />
    </Fragment>
  )
}

MetadataUsage.Main = Main
MetadataUsage.loader = loader
MetadataUsage.action = action
MetadataUsage.routeId = routeId
